// Google Tag Manager utilities for tracking

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Ensure dataLayer exists
const getDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/**
 * Track a page view event
 */
export const trackPageView = (path: string, title?: string) => {
  getDataLayer().push({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

/**
 * Track a custom event
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, unknown>
) => {
  getDataLayer().push({
    event: eventName,
    ...params,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (
  buttonName: string,
  location?: string,
  additionalParams?: Record<string, unknown>
) => {
  trackEvent('button_click', {
    button_name: buttonName,
    click_location: location,
    ...additionalParams,
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (
  formName: string,
  success: boolean,
  additionalParams?: Record<string, unknown>
) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_success: success,
    ...additionalParams,
  });
};

/**
 * Track CTA clicks
 *
 * GTM's data model keeps every key ever pushed until the page reloads, so a
 * param one CTA sends (the pricing cards' `plan` / `billing_cycle`) would
 * otherwise be read by GTM's Data Layer Variables on every later cta_click on
 * the same page (e.g. after a Ctrl/Cmd-click opened a plan card in a new tab).
 * Reset every optional CTA param on each push; callers override via
 * `additionalParams`.
 */
export const trackCTAClick = (
  ctaName: string,
  destination?: string,
  additionalParams?: Record<string, unknown>
) => {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_destination: destination,
    cta_location: undefined,
    plan: undefined,
    billing_cycle: undefined,
    ...additionalParams,
  });
};

/**
 * Track navigation events
 */
export const trackNavigation = (
  from: string,
  to: string,
  navType: 'header' | 'footer' | 'sidebar' | 'breadcrumb' | 'internal'
) => {
  trackEvent('navigation', {
    nav_from: from,
    nav_to: to,
    nav_type: navType,
  });
};

/**
 * Track user engagement
 */
export const trackEngagement = (
  action: 'scroll_depth' | 'time_on_page' | 'video_play' | 'file_download',
  value: string | number,
  additionalParams?: Record<string, unknown>
) => {
  trackEvent('user_engagement', {
    engagement_action: action,
    engagement_value: value,
    ...additionalParams,
  });
};
